import PublicationRecord from "models/view-models/publication-record";
import React, { PropsWithChildren, ReactNode, useState } from "react";
import { useLocalization } from "utilities/hooks/use-localization";
import StringUtils from "utilities/string-utils";
import Button from "atoms/buttons/button";
import { t } from "utilities/localization-utils";
import { ButtonStyles } from "atoms/constants/button-styles";
import usePublicationCover from "utilities/hooks/domain/publications/use-publication-cover";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import useUpdatePublicationPins from "utilities/hooks/domain/user-publication-pins/use-update-publication-pins";
import { ToastManager } from "utilities/toast/toast-manager";
import useModalActions from "utilities/hooks/use-modal-actions";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import UserPublicationPinRecord from "models/view-models/user-publication-pin-record";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";

const BASE_CLASS = "c-publication-selection-list-group-item";

export enum PublicationTextStyle {
    EditionFirst = "-edition-first",
    TitleFirst = "-title-first",
}

interface PublicationListItemProps {
    isNested?: boolean;
    publication: PublicationRecord;
    style: PublicationTextStyle;
    children?: ReactNode;
    pins?: UserPublicationPinRecord[];
    onChangePins: (pins: UserPublicationPinRecord[]) => void;
    onPublicationClick: (publication: PublicationRecord) => void;
}

const PublicationListItem: React.FunctionComponent<PublicationListItemProps> = (
    props
) => {
    const {
        children,
        isNested,
        publication,
        style,
        pins,
        onChangePins,
        onPublicationClick,
    } = props;

    const [isFocused, setIsFocused] = useState(false);
    const {
        isOpen: isUnpinConfirmationModalOpen,
        handleOpen: handleUnpinConfirmationModalOpen,
        handleClose: handleUnpinConfirmationModalClose,
    } = useModalActions();

    const { coverImageSrc } = usePublicationCover(publication);
    const { isSharedNotebook } = useCurrentNotebook();

    const hasCoverImageSrc = StringUtils.hasValue(coverImageSrc);

    const modifierClasses: string[] = [];

    if (isFocused) {
        modifierClasses.push(" -focused");
    }

    if (isNested) {
        modifierClasses.push(" -nested");
    }

    const handleTogglePinSuccess = (isPinned: boolean) => {
        ToastManager.success(
            t("success-actionResource", {
                action: isPinned ? t("pinned") : t("unpinned"),
                resource: t("publication").toLocaleLowerCase(),
            })
        );
    };

    const { isPinned, togglePin } = useUpdatePublicationPins(
        publication,
        pins ?? [],
        onChangePins,
        handleTogglePinSuccess
    );

    const Wrapper = ({ children }: PropsWithChildren) => {
        return (
            <div className={`${BASE_CLASS}__item -noLink ${style.toString()}`}>
                {children}
            </div>
        );
    };

    const handleTogglePin = async () => {
        if (isPinned) {
            handleUnpinConfirmationModalOpen();
        } else {
            await togglePin();
        }
    };

    const handleConfirmUnpin = async () => {
        await togglePin();
        handleUnpinConfirmationModalClose();
    };

    return (
        <>
            <div
                onClick={() => setIsFocused(true)}
                onMouseLeave={() => setIsFocused(false)}
                className={`${BASE_CLASS} ${modifierClasses.join(" ")}`}>
                <div className={`${BASE_CLASS}__item_parent`}>
                    <Wrapper>
                        {hasCoverImageSrc ? (
                            <img
                                className={`${BASE_CLASS}__cover`}
                                src={coverImageSrc}
                                alt={`${publication.getDisplayCodeEditionAndTitle()} cover`}
                                // @ts-ignore
                                loading="lazy"
                            />
                        ) : (
                            <div className={`${BASE_CLASS}__cover`} />
                        )}
                        <div>
                            <PublicationText
                                publication={publication}
                                style={style}>
                                {children}
                            </PublicationText>
                        </div>
                    </Wrapper>
                    <Button
                        cssClassName={`${BASE_CLASS}__selectButton`}
                        style={ButtonStyles.None}
                        onClick={() => onPublicationClick(publication)}>
                        <span className="sr-only">{t("view")}</span>
                    </Button>
                    {!isSharedNotebook && (
                        <Button
                            cssClassName={`${BASE_CLASS}__pinButton ${isPinned ? "-pinned" : ""}`}
                            onClick={handleTogglePin}
                            style={ButtonStyles.None}>
                            <span className="sr-only">{t("pin")}</span>
                            <Icon type={Icons.PushPin} />
                        </Button>
                    )}
                </div>
            </div>
            <ConfirmationModal
                label={t("pinned-publications--are-you-sure-unpin-label")}
                isVisible={isUnpinConfirmationModalOpen}
                message={t("pinned-publications--are-you-sure-unpin-message")}
                onCancel={handleUnpinConfirmationModalClose}
                onConfirm={handleConfirmUnpin}
            />
        </>
    );
};

function PublicationText(
    props: React.PropsWithChildren<
        Pick<PublicationListItemProps, "publication" | "style">
    >
) {
    const { children, publication, style } = props;
    const { t } = useLocalization();

    const edition = (
        <div className={`${BASE_CLASS}__item__edition`}>
            {publication.edition} {t("edition")}
        </div>
    );

    const isBold = style === PublicationTextStyle.TitleFirst;

    const title = (
        <div className={`${BASE_CLASS}__item__title${isBold ? " -bold" : ""}`}>
            {publication.getDisplayTitle(false)}
        </div>
    );

    if (style === PublicationTextStyle.EditionFirst) {
        return (
            <React.Fragment>
                {edition}
                {title}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {title}
            <div className={`${BASE_CLASS}__toggle`}>
                {edition}
                {children}
            </div>
        </React.Fragment>
    );
}

export default PublicationListItem;
